/**
 * Logo Upload Validation
 *
 * Client-side validation for logo uploads with strict constraints
 * to prevent storage explosion and ensure invoice-ready quality.
 */

import { logger } from '@/lib/logger';

// ============================================================================
// Constants
// ============================================================================

/** Maximum file size in bytes (2 MB) */
export const MAX_LOGO_SIZE_BYTES = 2 * 1024 * 1024;

/** Maximum file size for display */
export const MAX_LOGO_SIZE_DISPLAY = '2 MB';

/** Maximum dimensions (width or height) */
export const MAX_LOGO_DIMENSION = 4096;

/** Minimum dimensions for quality */
export const MIN_LOGO_DIMENSION = 50;

/** Allowed MIME types (raster formats, client-side processing) */
export const ALLOWED_LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;

/** SVG MIME type (requires server-side rasterization) */
export const SVG_MIME_TYPE = 'image/svg+xml';

/** All supported MIME types including SVG */
export const ALL_SUPPORTED_LOGO_TYPES = [...ALLOWED_LOGO_TYPES, SVG_MIME_TYPE] as const;

/** Allowed MIME types for display */
export const ALLOWED_LOGO_TYPES_DISPLAY = 'PNG, JPG, WebP, SVG';

// ============================================================================
// Types
// ============================================================================

export type LogoValidationErrorCode =
  | 'FILE_TOO_LARGE'
  | 'INVALID_TYPE'
  | 'SVG_NOT_SUPPORTED'
  | 'DIMENSIONS_TOO_LARGE'
  | 'DIMENSIONS_TOO_SMALL'
  | 'FAILED_TO_LOAD'
  | 'PHOTO_DETECTED'
  | 'ASPECT_RATIO_EXTREME';

export interface LogoValidationError {
  code: LogoValidationErrorCode;
  message: string;
  details?: string;
}

export interface LogoValidationResult {
  valid: boolean;
  error?: LogoValidationError;
  warning?: {
    code: 'PHOTO_DETECTED' | 'ASPECT_RATIO_EXTREME' | 'SVG_SERVER_PROCESSING';
    message: string;
  };
  metadata?: {
    width: number;
    height: number;
    bytes: number;
    contentType: string;
    hasAlpha: boolean;
  };
  /** True if this is an SVG that requires server-side rasterization */
  requiresServerProcessing?: boolean;
}

// ============================================================================
// Validation Functions
// ============================================================================

/**
 * Validate a logo file before upload
 * Returns validation result with metadata if valid
 */
export async function validateLogoFile(file: File): Promise<LogoValidationResult> {
  logger.log('[logoValidation] Validating file:', file.name, file.type, file.size);

  // Check file size
  if (file.size > MAX_LOGO_SIZE_BYTES) {
    return {
      valid: false,
      error: {
        code: 'FILE_TOO_LARGE',
        message: `Bestand is te groot (max ${MAX_LOGO_SIZE_DISPLAY})`,
        details: `Bestand: ${formatBytes(file.size)}, Maximum: ${MAX_LOGO_SIZE_DISPLAY}`,
      },
    };
  }

  // Check if SVG (requires server-side processing)
  if (file.type === SVG_MIME_TYPE) {
    // SVG is valid but requires server-side rasterization
    // We can't check dimensions client-side for SVG, so just validate size
    logger.log('[logoValidation] SVG detected, will require server-side rasterization');
    return {
      valid: true,
      requiresServerProcessing: true,
      warning: {
        code: 'SVG_SERVER_PROCESSING',
        message: 'SVG wordt geconverteerd naar PNG voor optimale weergave op facturen.',
      },
      metadata: {
        width: 0, // Unknown for SVG client-side
        height: 0,
        bytes: file.size,
        contentType: file.type,
        hasAlpha: true, // Assume SVG has transparency
      },
    };
  }

  // Check allowed types (raster formats)
  if (!ALLOWED_LOGO_TYPES.includes(file.type as (typeof ALLOWED_LOGO_TYPES)[number])) {
    return {
      valid: false,
      error: {
        code: 'INVALID_TYPE',
        message: `Ongeldig bestandstype (alleen ${ALLOWED_LOGO_TYPES_DISPLAY})`,
        details: `Ontvangen: ${file.type || 'onbekend'}`,
      },
    };
  }

  // Load image to check dimensions and detect if it's a photo
  try {
    const imageData = await loadImageData(file);

    // Check max dimensions
    if (imageData.width > MAX_LOGO_DIMENSION || imageData.height > MAX_LOGO_DIMENSION) {
      return {
        valid: false,
        error: {
          code: 'DIMENSIONS_TOO_LARGE',
          message: `Afbeelding is te groot (max ${MAX_LOGO_DIMENSION}×${MAX_LOGO_DIMENSION}px)`,
          details: `Afbeelding: ${imageData.width}×${imageData.height}px`,
        },
      };
    }

    // Check min dimensions
    if (imageData.width < MIN_LOGO_DIMENSION || imageData.height < MIN_LOGO_DIMENSION) {
      return {
        valid: false,
        error: {
          code: 'DIMENSIONS_TOO_SMALL',
          message: `Afbeelding is te klein (min ${MIN_LOGO_DIMENSION}×${MIN_LOGO_DIMENSION}px)`,
          details: `Afbeelding: ${imageData.width}×${imageData.height}px`,
        },
      };
    }

    // Detect if it looks like a photo (warning only, still allow)
    const isLikelyPhoto = detectPhoto(imageData, file);

    // Check for extreme aspect ratio (>3:1 or <1:3)
    const aspectRatio = imageData.width / imageData.height;
    const isExtremeAspectRatio = aspectRatio > 3 || aspectRatio < 1 / 3;

    const result: LogoValidationResult = {
      valid: true,
      metadata: {
        width: imageData.width,
        height: imageData.height,
        bytes: file.size,
        contentType: file.type,
        hasAlpha: imageData.hasAlpha,
      },
    };

    // Priority: extreme aspect ratio warning > photo warning
    if (isExtremeAspectRatio) {
      result.warning = {
        code: 'ASPECT_RATIO_EXTREME',
        message:
          'Dit logo heeft een extreme verhouding. Het kan te klein of vervormd lijken op facturen.',
      };
    } else if (isLikelyPhoto) {
      result.warning = {
        code: 'PHOTO_DETECTED',
        message:
          'Dit lijkt een foto te zijn. Voor de beste resultaten gebruik een transparante PNG of SVG.',
      };
    }

    logger.log('[logoValidation] Validation passed:', result.metadata);
    return result;
  } catch (error) {
    logger.error('[logoValidation] Failed to load image:', error);
    return {
      valid: false,
      error: {
        code: 'FAILED_TO_LOAD',
        message: 'Kon afbeelding niet laden',
        details: error instanceof Error ? error.message : 'Onbekende fout',
      },
    };
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

interface ImageData {
  width: number;
  height: number;
  hasAlpha: boolean;
  colorVariance: number;
}

/**
 * Load image and extract metadata
 */
function loadImageData(file: File): Promise<ImageData> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      // Create canvas to analyze image
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        URL.revokeObjectURL(url);
        reject(new Error('Could not create canvas context'));
        return;
      }

      // Use smaller size for analysis to save memory
      const maxAnalysisSize = 256;
      const scale = Math.min(1, maxAnalysisSize / Math.max(img.naturalWidth, img.naturalHeight));
      canvas.width = Math.floor(img.naturalWidth * scale);
      canvas.height = Math.floor(img.naturalHeight * scale);

      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      let hasAlpha = false;
      let colorVariance = 0;

      try {
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        // Check for alpha channel and calculate color variance
        let alphaCount = 0;
        const colorSamples: number[] = [];

        for (let i = 0; i < data.length; i += 4) {
          // Check alpha
          if (data[i + 3] !== undefined && data[i + 3] < 255) {
            alphaCount++;
          }

          // Sample colors for variance calculation (every 16th pixel)
          if (i % 64 === 0) {
            const r = data[i] ?? 0;
            const g = data[i + 1] ?? 0;
            const b = data[i + 2] ?? 0;
            colorSamples.push(r + g + b);
          }
        }

        hasAlpha = alphaCount > (data.length / 4) * 0.01; // >1% transparent pixels

        // Calculate variance
        if (colorSamples.length > 0) {
          const mean = colorSamples.reduce((a, b) => a + b, 0) / colorSamples.length;
          const squaredDiffs = colorSamples.map((v) => Math.pow(v - mean, 2));
          colorVariance = Math.sqrt(squaredDiffs.reduce((a, b) => a + b, 0) / colorSamples.length);
        }
      } catch {
        // Canvas tainted by CORS - can't analyze, assume no alpha
        logger.warn('[logoValidation] Could not analyze image data (CORS)');
      }

      URL.revokeObjectURL(url);

      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
        hasAlpha,
        colorVariance,
      });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}

/**
 * Detect if image is likely a photo rather than a logo
 * Heuristics:
 * - Large file size relative to dimensions
 * - No transparency
 * - High color variance
 * - JPEG format with large dimensions
 */
function detectPhoto(imageData: ImageData, file: File): boolean {
  // JPEG without transparency and large dimensions
  if (file.type === 'image/jpeg' && imageData.width > 1000 && imageData.height > 1000) {
    return true;
  }

  // Large file size relative to dimensions (photos are typically >0.5 bytes per pixel)
  const pixelCount = imageData.width * imageData.height;
  const bytesPerPixel = file.size / pixelCount;
  if (bytesPerPixel > 0.5 && !imageData.hasAlpha) {
    return true;
  }

  // High color variance without transparency suggests photo
  if (imageData.colorVariance > 100 && !imageData.hasAlpha) {
    return true;
  }

  return false;
}

/**
 * Format bytes to human readable string
 */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Get user-friendly error message for validation error
 */
export function getValidationErrorMessage(error: LogoValidationError): string {
  return error.message;
}

/**
 * Check if file type is allowed (quick check without loading image)
 */
export function isAllowedLogoType(file: File): boolean {
  return ALLOWED_LOGO_TYPES.includes(file.type as (typeof ALLOWED_LOGO_TYPES)[number]);
}

/**
 * Check if file size is within limits (quick check)
 */
export function isWithinSizeLimit(file: File): boolean {
  return file.size <= MAX_LOGO_SIZE_BYTES;
}
